import MetaIntegrationForm from "./IntegrationForms/MetaIntegrationForm.jsx";
import MySQLLeadIntegrationForm from "./IntegrationForms/MySQLLeadIntegrationForm.jsx";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import toast from "react-hot-toast";
import GoogleIntegrationForm from "./IntegrationForms/GoogleIntegrationForm.jsx";
import BsShopIntegrationForm from "./IntegrationForms/BsShopIntegrationForm.jsx";
import ShoptetIntegrationForm from "./IntegrationForms/ShoptetIntegrationForm.jsx";
import SklikIntegrationForm from "./IntegrationForms/SklikIntegrationForm.jsx";
import HeurekaIntegrationForm from "./IntegrationForms/HeurekaIntegrationForm.jsx";
import UpgatesIntegrationForm from "./IntegrationForms/UpgatesIntegrationForm.jsx";
import {isPreview} from "../../../utils/environment.js";
import PageTitle from "../../../components/PageTitle.jsx";
import {useTranslation} from "react-i18next";
import GlamiIntegrationForm from "./IntegrationForms/GlamiIntegrationForm.jsx";
import ZboziIntegrationForm from "./IntegrationForms/ZboziIntegrationForm.jsx";
import MicrosoftIntegrationForm from "./IntegrationForms/MicrosoftIntegrationForm.jsx";
import PinterestIntegrationForm from "./IntegrationForms/PinterestIntegrationForm.jsx";
import TikTokIntegrationForm from "./IntegrationForms/TikTokIntegrationForm.jsx";
import CriteoIntegrationForm from "./IntegrationForms/CriteoIntegrationForm.jsx";
import {useIsAdministrator} from "../../../components/auth/rolesAndPermissions.js";
import {isBrandVisibleToUser} from "../../../utils/brands/brandVisibility.js";
import GoogleDataIntegrationForm from "./IntegrationForms/GoogleDataIntegrationForm.jsx";
import {IntegrationAdminMetadata, IntegrationSection} from "./IntegrationDetailUi.jsx";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {ProjectIntegrationsApi} from "../../../api/authority/generated/index.js";
import {refreshOAuthIntegrationResources, reloadProjectIntegration} from "./refreshOAuthIntegrationResources.js";
import Button from "../../../components/Button.jsx";
import YesNo from "../../../components/YesNo.jsx";

import {canManageIntegrationAction, isShoptetAddonIntegration} from "./integrationListPresentation.js";
import {isGoogleAdsOAuthReconnectError} from "./googleAdsOAuthStatus.js";

export default function ThirdPartyServiceEditForm({project, brand, projectIntegration, onIntegrationChanged}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let isAdministrator = useIsAdministrator();
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const navigate = useNavigate();
    const [displayedIntegration, setDisplayedIntegration] = useState(projectIntegration);
    const [confirmation, setConfirmation] = useState(null);
    const [lifecycleLoading, setLifecycleLoading] = useState(false);

    useEffect(() => {
        setDisplayedIntegration(projectIntegration);
    }, [projectIntegration]);

    const refreshOAuthResources = async () => {
        const api = new ProjectIntegrationsApi(getAuthorityConfiguration());
        return refreshOAuthIntegrationResources({
            api,
            projectId: project.id,
            integrationId: displayedIntegration.id,
            onIntegrationRefreshed: setDisplayedIntegration,
        });
    };

    const reloadIntegrationDetail = async () => {
        const api = new ProjectIntegrationsApi(getAuthorityConfiguration());
        return reloadProjectIntegration({
            api,
            projectId: project.id,
            integrationId: displayedIntegration.id,
            onIntegrationRefreshed: (integration) => {
                setDisplayedIntegration(integration);
                onIntegrationChanged?.(integration);
            },
        });
    };

    const runLifecycleAction = async (action) => {
        if (lifecycleLoading || !canManageIntegrationAction(displayedIntegration, action)) return;
        setLifecycleLoading(true);
        try {
            const api = new ProjectIntegrationsApi(getAuthorityConfiguration());
            const request = {projectId: project.id, integrationId: displayedIntegration.id};
            if (action === 'pause') await api.pauseProjectIntegration(request);
            if (action === 'resume') await api.resumeProjectIntegration(request);
            if (action === 'delete') {
                await api.deleteProjectIntegration(request);
                toast.success(t('project.integration-removed'));
                navigate(`/project/${project.id}/settings/integrations`);
                return;
            }
            await reloadIntegrationDetail();
            toast.success(action === 'pause'
                ? t('project.integration-paused-success', {defaultValue: 'Integrace byla pozastavena.'})
                : t('project.integration-resumed-success', {defaultValue: 'Integrace byla znovu spuštěna.'}));
        } catch (error) {
            const message = await et(error);
            if (message) toast.error(message);
        } finally {
            setLifecycleLoading(false);
        }
    };

    let [errCount, setErrCount] = useState({});
    let [translatedErrors, setTranslatedErrors] = useState({});

    const forms = {
        meta: <MetaIntegrationForm project={project} projectIntegration={displayedIntegration} refreshOAuthResources={refreshOAuthResources}/>,
        mysql: <MySQLLeadIntegrationForm project={project} projectIntegration={projectIntegration}/>,
        google: <GoogleIntegrationForm project={project} projectIntegration={displayedIntegration} refreshOAuthResources={refreshOAuthResources}/>,
        microsoft: <MicrosoftIntegrationForm project={project} projectIntegration={displayedIntegration} refreshOAuthResources={refreshOAuthResources}/>,
        pinterest: <PinterestIntegrationForm project={project} projectIntegration={displayedIntegration} refreshOAuthResources={refreshOAuthResources}/>,
        tiktok: <TikTokIntegrationForm project={project} projectIntegration={displayedIntegration} refreshOAuthResources={refreshOAuthResources}/>,
        criteo: <CriteoIntegrationForm project={project} projectIntegration={displayedIntegration} refreshOAuthResources={refreshOAuthResources}/>,
        'google-merchant': <GoogleDataIntegrationForm project={project} projectIntegration={displayedIntegration} brand={brand} reloadIntegrationDetail={reloadIntegrationDetail}/>,
        'google-analytics': <GoogleDataIntegrationForm project={project} projectIntegration={displayedIntegration} brand={brand} reloadIntegrationDetail={reloadIntegrationDetail}/>,
        'google-search-console': <GoogleDataIntegrationForm project={project} projectIntegration={displayedIntegration} brand={brand} reloadIntegrationDetail={reloadIntegrationDetail}/>,
        'google-bigquery': <GoogleDataIntegrationForm project={project} projectIntegration={displayedIntegration} brand={brand} reloadIntegrationDetail={reloadIntegrationDetail}/>,
        sklik: <SklikIntegrationForm project={project} projectIntegration={projectIntegration}/>,
        zbozi: <ZboziIntegrationForm project={project} projectIntegration={projectIntegration}/>,
        glami: <GlamiIntegrationForm project={project} projectIntegration={projectIntegration}/>,
        heureka: <HeurekaIntegrationForm project={project} projectIntegration={projectIntegration}/>,
        bsshop: <BsShopIntegrationForm project={project} projectIntegration={projectIntegration}/>,
        shoptet: <ShoptetIntegrationForm project={project} projectIntegration={projectIntegration}/>,
        upgates: <UpgatesIntegrationForm project={project} projectIntegration={projectIntegration}/>,
    };

    const form = forms[brand.slug] || null;

    useEffect(() => {
        setTranslatedErrors({});
        if (displayedIntegration && displayedIntegration.errors) {
            for (let error of displayedIntegration.errors) {
                if (brand.slug === 'google' && isGoogleAdsAccountAvailabilityError(error)) {
                    continue;
                }
                if (brand.slug === 'google' && isGoogleAdsOAuthReconnectError(error)) {
                    setTranslatedErrors((currentErrors) => ({
                        ...currentErrors,
                        oauthReconnectRequired: t('project.google.oauth-reconnect-required-error'),
                    }));
                    continue;
                }
                et(error).then((message) => {
                    setTranslatedErrors((translatedErrors) => {
                        translatedErrors = {...translatedErrors, [JSON.stringify(message)]: message};
                        setErrCount(errCount + 1);
                        return translatedErrors;
                    });
                });
            }
        }

    }, [brand.slug, displayedIntegration?.errors]);

    if (isPreview()) {
        return <PageTitle>{t('error.endpoint-not-allowed-on-demo')}</PageTitle>;
    }

    if (!isBrandVisibleToUser(brand, isAdministrator, Boolean(projectIntegration))) {
        return null;
    }

    return (<div className={'flex flex-col gap-5'}>
        {isAdministrator && projectIntegration && (
            <IntegrationAdminMetadata integrationId={projectIntegration.id}/>
        )}
        {Object.values(translatedErrors).map((error, key) => {
            return <div key={'error-' + key} className={'rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-datixo-negative dark:border-red-800 dark:bg-red-950/30 text-datixo-negative'}>{error}</div>;
        })}
        <div className={'integration-detail-form [&_.form-input]:rounded-lg [&_.form-input]:border-slate-300 [&_.form-input]:bg-white [&_.form-input]:shadow-sm dark:[&_.form-input]:border-slate-600 dark:[&_.form-input]:bg-slate-900'}>
            {form}
        </div>
        {displayedIntegration && <IntegrationSection
            title={t('project.integration-management-title', {defaultValue: 'Správa integrace'})}
            description={isShoptetAddonIntegration(displayedIntegration)
                ? displayedIntegration.status === 'inactive'
                    ? t('project.shoptet-addon-inactive-description', {defaultValue: 'Doplněk je odinstalovaný. Znovu jej nainstalujte v administraci Shoptetu. Zde můžete odstranit jeho propojení s projektem.'})
                    : t('project.shoptet-addon-managed-in-shoptet', {defaultValue: 'Doplněk spravujte v administraci Shoptetu. Pro odinstalaci otevřete Propojení → Správce doplňků → Marketing → Datixo → Odinstalovat.'})
                : displayedIntegration.paused
                ? t('project.integration-paused-description', {defaultValue: 'Integrace je pozastavená. Oprávnění, nastavení a dosud stažená data zůstávají zachována.'})
                : t('project.integration-management-description', {defaultValue: 'Pozastavení zastaví další plánované stahování, ale zachová připojení, nastavení i historii.'})}
            className={'border-slate-300'}
        >
            <div className={'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'}>
                {!isShoptetAddonIntegration(displayedIntegration) && <Button
                    variant={displayedIntegration.paused ? 'outline-violet' : 'outline-warning'}
                    disabled={lifecycleLoading}
                    onClick={() => displayedIntegration.paused
                        ? runLifecycleAction('resume')
                        : setConfirmation('pause')}
                >
                    {displayedIntegration.paused
                        ? t('project.integration-resume', {defaultValue: 'Znovu spustit integraci'})
                        : t('project.integration-pause', {defaultValue: 'Pozastavit integraci'})}
                </Button>}
                {canManageIntegrationAction(displayedIntegration, 'delete') && <Button variant={'outline-red'} disabled={lifecycleLoading} onClick={() => setConfirmation('delete')}>
                    {t('project.integration-delete', {defaultValue: 'Smazat integraci'})}
                </Button>}
            </div>
        </IntegrationSection>}
        {displayedIntegration && <YesNo
            visible={confirmation !== null && canManageIntegrationAction(displayedIntegration, confirmation)}
            setVisible={(visible) => { if (!visible) setConfirmation(null); }}
            loading={lifecycleLoading}
            dangerous
            title={confirmation === 'delete'
                ? t('project.integration-delete-confirm-title', {defaultValue: 'Smazat integraci?'})
                : t('project.integration-pause-confirm-title', {defaultValue: 'Pozastavit integraci?'})}
            question={confirmation === 'delete'
                ? brand.slug === 'google'
                    ? t('project.google.integration-delete-confirm')
                    : t('project.integration-delete-confirm', {defaultValue: 'Integrace a její nastavení budou odstraněny. Tuto akci nelze vrátit zpět.'})
                : t('project.integration-pause-confirm', {defaultValue: 'Plánované stahování se zastaví. Připojení, nastavení a dosud publikovaná data zůstanou zachována.'})}
            onYes={() => runLifecycleAction(confirmation)}
        />}
    </div>);
}

export function isGoogleAdsAccountAvailabilityError(error) {
    const serializedError = typeof error === 'string' ? error : JSON.stringify(error) ?? '';

    return serializedError.includes('GOOGLE_ADS_ACCOUNT_PERMISSION_DENIED:');
}
